import { useEffect, useState } from "react";
import {
  Container,
  Table,
  Badge,
  Button,
  Modal,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import { motion } from "framer-motion";
import { FaFileInvoice, FaUndo, FaEye } from "react-icons/fa";
import axios from "axios";

import "./order.css";
import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";

const API = "https://admin.soulliqo.com";

const Order = () => {
  const [orders, setOrders] = useState([]);
  const [viewOrder, setViewOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(`${API}/admin/orders`, {
        withCredentials: true,
      });

      if (!res?.data || !Array.isArray(res.data.orders)) {
        console.error("Invalid API response:", res?.data);
        setOrders([]);
        setError("Invalid response from server");
        return;
      }

      const formatted = res.data.orders.map((order) => {
        const customerName = order?.address
          ? `${order.address.firstName || ""} ${order.address.lastName || ""}`.trim()
          : order?.user
          ? `${order.user.firstName || ""} ${order.user.lastName || ""}`.trim()
          : "Unknown";

        return {
          ...order, // keep full object
          customer: customerName || "Unknown",
          email: order?.address?.email || order?.user?.email || "N/A",
          productsText: Array.isArray(order?.items)
            ? order.items
                .map((i) => i?.product?.name || i?.box?.name)
                .filter(Boolean)
                .join(", ")
            : "N/A",
          totalText: `₹${(order?.totalAmount || 0).toFixed(2)}`,
          delivery: order?.status || "Pending",
        };
      });

      setOrders(formatted);
    } catch (err) {
      console.error("Failed to load orders", err);
      setOrders([]);
      setError(err?.response?.data?.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleDeliveryChange = async (orderId, status) => {
    if (!orderId) return;

    setUpdatingId(orderId);

    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status },
        { withCredentials: true }
      );

      setOrders((prev) =>
        prev.map((order) =>
          order._id === orderId
            ? { ...order, delivery: status, status: status }
            : order
        )
      );

      // update modal order too
      setViewOrder((prev) =>
        prev && prev._id === orderId ? { ...prev, delivery: status, status } : prev
      );
    } catch (err) {
      console.error("Update status failed:", err);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to update delivery status";

      alert(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleInvoiceDownload = (orderId) => {
    if (!orderId) return;
    window.open(`${API}/admin/orders/invoice/${orderId}`, "_blank");
  };

  return (
    <>
      <Header />

      <div className="admin-layout">
        <Sidebar />

        <main className="admin-content">
          <Container fluid>
            <motion.h4
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              Orders
            </motion.h4>

            {loading && (
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <span>Loading orders...</span>
              </div>
            )}

            {!loading && error && <p className="text-danger">{error}</p>}

            {!loading && !error && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="table-wrapper"
              >
                <Table responsive bordered hover className="orders-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Products</th>
                      <th>Payment</th>
                      <th>Total</th>
                      <th>Delivery</th>
                      <th>Invoice / Refund</th>
                    </tr>
                  </thead>

                  <tbody>
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          No orders found
                        </td>
                      </tr>
                    )}

                    {orders.map((order, index) => (
                      <motion.tr
                        key={order._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <td>{order._id}</td>
                        <td>
                          <div className="fw-bold">{order.customer}</div>
                          <div className="text-muted small">{order.email}</div>
                        </td>

                        <td style={{ maxWidth: "250px" }}>
                          <div className="text-truncate">{order.productsText}</div>
                        </td>

                        <td>
                          <Badge bg="success">Paid</Badge>
                        </td>

                        <td>{order.totalText}</td>

                        <td className="d-flex align-items-center gap-2">
                          <select
                            className="form-select form-select-sm"
                            value={order.delivery}
                            onChange={(e) =>
                              handleDeliveryChange(order._id, e.target.value)
                            }
                            disabled={
                              order.delivery === "Delivered" ||
                              updatingId === order._id
                            }
                          >
                            <option value="Pending">Pending</option>
                            <option value="Preparing">Preparing</option>
                            <option value="Shipped">Shipped</option>
                            <option value="Delivered">Delivered</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>

                          {updatingId === order._id && (
                            <Spinner animation="border" size="sm" />
                          )}
                        </td>

                        <td className="action-buttons d-flex gap-2">
                          {/* View Details */}
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setViewOrder(order)}
                          >
                            <FaEye />
                          </Button>

                          {/* Invoice */}
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => handleInvoiceDownload(order._id)}
                          >
                            <FaFileInvoice />
                          </Button>

                          {/* Refund Placeholder */}
                          <Button size="sm" variant="outline-danger" disabled>
                            <FaUndo />
                          </Button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </Table>
              </motion.div>
            )}
          </Container>
        </main>
      </div>

      {/* ✅ VIEW ORDER MODAL */}
      <Modal
        show={!!viewOrder}
        onHide={() => setViewOrder(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Order Details</Modal.Title>
        </Modal.Header>

        {viewOrder && (
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-1">
                  <strong>Order ID:</strong> {viewOrder._id}
                </p>
                <p className="mb-1">
                  <strong>Status:</strong>{" "}
                  <Badge bg="info">{viewOrder.delivery}</Badge>
                </p>
              </Col>

              <Col md={6}>
                <p className="mb-1">
                  <strong>Total:</strong> ₹{(viewOrder.totalAmount || 0).toFixed(2)}
                </p>
                <p className="mb-1">
                  <strong>Payment:</strong>{" "}
                  <Badge bg="success">Paid</Badge>
                </p>
              </Col>
            </Row>

            <hr />

            {/* ✅ Address */}
            <h6 className="mb-2">📦 Shipping Address</h6>

            {viewOrder.address ? (
              <div className="border rounded p-3 mb-4 bg-light">
                <div className="fw-bold">
                  {viewOrder.address.firstName || ""}{" "}
                  {viewOrder.address.lastName || ""}
                </div>
                <div className="text-muted">{viewOrder.address.email || "N/A"}</div>
                <div>{viewOrder.address.mobile || "N/A"}</div>
                <div>{viewOrder.address.street || ""}</div>
                <div>
                  {viewOrder.address.city || ""},{" "}
                  {viewOrder.address.state || ""}
                </div>
                <div>{viewOrder.address.pincode || ""}</div>
              </div>
            ) : (
              <p className="text-muted">No address available</p>
            )}

            {/* ✅ Items */}
            <h6 className="mb-3">🛒 Products</h6>

            {Array.isArray(viewOrder.items) && viewOrder.items.length > 0 ? (
              viewOrder.items.map((item, idx) => {
                const name = item?.product?.name || item?.box?.name || "Item";
                const qty = item?.quantity || 1;
                const price = item?.price || item?.product?.price || item?.box?.price || 0;

                // const imagePath = item?.product?.image;
                // const imageUrl = imagePath
                //   ? imagePath.startsWith("http")
                //     ? imagePath
                //     : `${imagePath}`
                //   : "https://via.placeholder.com/60";

                return (
                  <div
                    key={idx}
                    className="d-flex align-items-center gap-3 border rounded p-2 mb-2"
                  >
                    {/* <img
                      src={imageUrl}
                      alt="product"
                      width="60"
                      height="60"
                      style={{
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    /> */}

                    <div style={{ flex: 1 }}>
                      <div className="fw-bold">{name}</div>
                      <div className="text-muted small">
                        Qty: {qty} | Price: ₹{price}
                      </div>
                    </div>

                    <div className="fw-bold">₹{(qty * price).toFixed(2)}</div>
                  </div>
                );
              })
            ) : (
              <p className="text-muted">No items found</p>
            )}
          </Modal.Body>
        )}

        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="secondary" onClick={() => setViewOrder(null)}>
            Close
          </Button>

          {viewOrder && (
            <Button
              variant="success"
              onClick={() => handleInvoiceDownload(viewOrder._id)}
            >
              <FaFileInvoice className="me-2" />
              Download Invoice
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Order;
